import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, Loader2, UploadCloud } from "lucide-react";

import { API, SENDING_ON } from "../lib/portalMode";

export default function ProviderSignatureManager({ authToken, onClose }) {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [current, setCurrent] = useState(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const authHeaders = { headers: { Authorization: `Bearer ${authToken}` } };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portal/provider-signature`, authHeaders);
      setCurrent(res.data?.exists ? res.data.image_data_url : null);
    } catch (e) {
      toast.error("Could not load the current signature.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg"].includes(f.type)) {
      toast.error("Please choose a PNG or JPG image.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const upload = async () => {
    if (!SENDING_ON) return; // off the live portal nothing is sent (lib/portalMode.js)
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/portal/provider-signature`, fd, {
        headers: { ...authHeaders.headers },
      });
      toast.success("Signature updated.");
      setFile(null);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" data-testid="provider-signature-modal">
      <div className="bg-white rounded-xl max-w-xl w-full my-6 shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Provider signature</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="provider-signature-close-btn"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-6">
          <p className="text-sm text-slate-500">
            This signature appears on the two NDIS service-agreement PDFs. It is stored privately and is never kept in the code repository.
          </p>

          <div>
            <div className="text-sm font-medium text-slate-700 mb-2">Current signature</div>
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 min-h-[90px] flex items-center justify-center" data-testid="provider-signature-current">
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              ) : current ? (
                <img src={current} alt="Current provider signature" className="max-h-24" data-testid="provider-signature-current-img" />
              ) : (
                <span className="text-sm text-slate-400" data-testid="provider-signature-none">No signature uploaded yet.</span>
              )}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-slate-700 mb-2">Upload a new PNG or JPG</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={onPick}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-800 file:text-white file:text-sm"
              data-testid="provider-signature-file-input"
            />
            {preview && (
              <div className="mt-3 border border-slate-200 rounded-lg p-4 bg-white flex items-center justify-center" data-testid="provider-signature-preview">
                <img src={preview} alt="New signature preview" className="max-h-24" />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} data-testid="provider-signature-cancel-btn">Cancel</Button>
            <Button onClick={upload} disabled={!file || uploading || !SENDING_ON} data-testid="provider-signature-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}
              Save signature
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
